import { createContext, useContext, useCallback, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useLoginMutation,
  useLogoutMutation,
  setSessionMetadata,
  clearSessionMetadata,
  getSessionMetadata,
  getUserData,
  type UserProfile,
} from '@/services/api';

/**
 * Auth Context Value Interface
 * Session-based authentication (HTTP-only cookies)
 * NOTE: Auth state is managed via backend session cookies.
 * Any 401 error from API calls will automatically redirect to login.
 */
export interface AuthContextValue {
  /** Current authenticated user (from login response) */
  user: UserProfile | null;
  /** Loading state */
  isLoading: boolean;
  /** Whether the initial session metadata check has completed */
  isInitialized: boolean;
  /** Authenticated state (based on stored session metadata) */
  isAuthenticated: boolean;
  /** Login function */
  login: (email: string, password: string) => Promise<void>;
  /** Logout function */
  logout: () => Promise<void>;
  /** Set user data */
  setUser: (user: UserProfile | null) => void;
}

/**
 * Auth Context
 */
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Auth Provider Props
 */
export interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth Provider Component
 * Provides authentication context to the entire application
 * Integrates with RTK Query API for auth operations
 *
 * @example
 * ```tsx
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 * ```
 */
export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const navigate = useNavigate();

  // Local state for user (stored after login)
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // RTK Query Hooks - Session-based authentication
  const [loginMutation, { isLoading: isLoginLoading }] = useLoginMutation();
  const [logoutMutation, { isLoading: isLogoutLoading }] = useLogoutMutation();

  // Check if user session exists on mount (from sessionStorage metadata)
  useEffect(() => {
    const userEmail = getSessionMetadata();
    if (userEmail) {
      // User has session metadata, mark as authenticated
      // Actual session validation happens on API calls
      // If session expired, 401 error will redirect to login
      const storedUser = getUserData() as Partial<UserProfile> & Record<string, any>;
      setUser({
        id: storedUser.id ?? 0,
        email: storedUser.email || userEmail,
        fullName:
          storedUser.fullName ||
          `${storedUser.first_name || ''} ${storedUser.last_name || ''}`.trim() ||
          userEmail,
        avatar: storedUser.avatar,
        roles: storedUser.roles || [],
        isVerified: storedUser.isVerified ?? storedUser.is_email_verified,
        createdAt: storedUser.createdAt || storedUser.created_at,
        updatedAt: storedUser.updatedAt || storedUser.updated_at,
      });
    }
    setIsInitialized(true);
  }, []);

  /**
   * Login function
   * Authenticates user with email and password
   * Backend creates session via HTTP-only cookie
   */
  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      try {
        const response = await loginMutation({ email, password }).unwrap();

        // Handle new response structure
        if (response.result?.admin) {
          const admin = response.result.admin;

          // Check if email is verified
          if (!admin.is_email_verified) {
            throw new Error(
              'Please verify your email before logging in. Check your inbox for the verification link.'
            );
          }

          // Check if account is active
          if (!admin.is_active) {
            throw new Error('Your account has been deactivated. Please contact support.');
          }

          // Check if account is suspended
          if (admin.is_suspended) {
            throw new Error('Your account has been suspended. Please contact support.');
          }

          // Store user data
          const userData: UserProfile = {
            id: admin.id,
            email: admin.email,
            fullName: admin.fullName || `${admin.first_name} ${admin.last_name}`,
            avatar: admin.avatar,
            roles: admin.roles || [],
            isVerified: admin.is_email_verified,
            createdAt: admin.created_at,
            updatedAt: admin.updated_at,
          };

          setUser(userData);
          setSessionMetadata(admin.email, admin);

          // Navigate to dashboard after successful login
          navigate('/app/dashboard');
        } else {
          throw new Error(response.message || 'Login failed');
        }
      } catch (err: any) {
        const errorMessage =
          err?.data?.message || err?.message || 'Login failed. Please check your credentials.';
        throw new Error(errorMessage);
      }
    },
    [loginMutation, navigate]
  );

  /**
   * Logout function
   * Backend destroys session and clears HTTP-only cookie
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      // Call logout API - backend destroys session
      await logoutMutation().unwrap();
    } catch (err) {
      // Continue with logout even if API call fails
      console.error('Logout API error:', err);
    } finally {
      // Clear client-side data
      setUser(null);
      clearSessionMetadata();

      // Navigate to login
      navigate('/auth/login');
    }
  }, [logoutMutation, navigate]);

  // Determine loading state
  const isLoading = !isInitialized || isLoginLoading || isLogoutLoading;

  // Determine authenticated state (user exists and has session metadata)
  const isAuthenticated = isInitialized && !!user && !!getSessionMetadata();

  const value: AuthContextValue = {
    user,
    isLoading,
    isInitialized,
    isAuthenticated,
    login,
    logout,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * useAuth Hook
 * Access auth context
 * Must be used within AuthProvider
 *
 * @throws Error if used outside AuthProvider
 *
 * @example
 * ```tsx
 * const { user, isAuthenticated, login, logout } = useAuth();
 * ```
 */
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      'useAuth must be used within an AuthProvider. Make sure to wrap your app with <AuthProvider>.'
    );
  }

  return context;
};

export default AuthProvider;
